import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';

import AssetDM1FaultsDisplay from './AssetDM1FaultsDisplay';
import RequestHelper from '../../Lib/AlertAndDmRequestHelper';

const SORT_DIRECTIONS = ['ASC', 'DESC'];
const limit = 20;

const initState = {
  dataLoading: true,
  selectedStatus: 'positive',
  ruleIdFilter: null,
  sort: 'TriggeredAt',
  order: 'DESC',
  page: 1,
  dataCount: 0,
  error: '',
  spnFilter: null,
  fmiFilter: null,
  saFilter: null,
  canbusFilter: null,
};

const refreshState = {
  dataLoading: true,
  page: 1,
  dataCount: 0,
  data: [],
};

export default function AssetDM1FaultsContainer(props) {
  const {
    asset: { _id: assetID, orgID },
  } = props;

  const [state, updateState] = useState(initState);
  const [error, setError] = useState('');
  const spnFilterRef = useRef(null);
  const fmiFilterRef = useRef(null);
  const saFilterRef = useRef(null);
  const canbusFilterRef = useRef(null);
  const [showMobileFilters, toggleMobileFilters] = useState(false);

  let source = CancelToken.source();

  const handleSortClick = (nextSortBy) => {
    const { sort } = state;
    switch (true) {
      case nextSortBy === sort: {
        const [order] = SORT_DIRECTIONS.filter((d) => d !== state.order);
        updateAlertsState({ page: 1, order });
        break;
      }
      default:
        updateAlertsState({
          page: 1,
          sort: nextSortBy,
          order: 'DESC',
        });
    }
  };

  const setSelectedStatus = (selectedStatus, mobileView) => {
    if (selectedStatus === state.selectedStatus) return;
    updateState({ ...state, ...refreshState, selectedStatus });
    mobileView && toggleMobileFilters(false);
  };

  const setPage = (page) => {
    if (page === state.page) return;
    updateAlertsState({ page });
  };

  const setDmFilter = (value, filter) => {
    if (value) {
      updateState({
        ...state,
        ...refreshState,
        [`${filter}Filter`]: value.value,
      });
    } else {
      updateState({ ...state, ...refreshState, [`${filter}Filter`]: null });
    }
  };

  const fetchAlertMetrics = async () => {
    try {
      const request = new RequestHelper({
        ...state,
        orgID,
        gatewayIdFilter: assetID,
      });
      const { Count } = await request.fetchDM1Metrics(source.token);
      return Count || 0;
    } catch (err) {
      if (!isCancel(err)) {
        setError(err.response?.data?.message ?? err.message);
      }
      return 0;
    }
  };

  const fetchDM1s = async ({ page, sort, order } = {}) => {
    try {
      const request = new RequestHelper({
        ...state,
        orgID,
        orgIdFilter: orgID,
        limit,
        gatewayIdFilter: assetID,
        page: page || state.page,
        sort: sort || state.sort,
        order: order || state.order,
      });
      const DmAlerts = await request.fetchDM1s(source.token);
      return DmAlerts || [];
    } catch (err) {
      if (!isCancel(err)) {
        setError(err.response?.data?.message ?? err.message);
      }
      return [];
    }
  };

  const updateCountAndAlertsState = async () => {
    try {
      const [dataCount, data] = await Promise.all([
        fetchAlertMetrics(),
        fetchDM1s(),
      ]);
      updateState({ ...state, dataCount, data, dataLoading: false });
    } catch (err) {
      setError(err.message);
      updateState({ ...state, dataLoading: false });
    }
  };

  const updateAlertsState = async (params) => {
    try {
      const data = await fetchDM1s(params);
      updateState({ ...state, data, ...params, dataLoading: false });
    } catch (err) {
      setError(err.message);
      updateState({ ...state, dataLoading: false });
    }
  };

  useEffect(() => {
    if (!assetID) return;
    source = CancelToken.source();
    updateCountAndAlertsState();
  }, [
    assetID,
    state.selectedStatus,
    state.saFilter,
    state.fmiFilter,
    state.spnFilter,
    state.canbusFilter,
  ]);

  return (
    <AssetDM1FaultsDisplay
      {...state}
      {...props}
      tab="dm1"
      handleStatusSelected={setSelectedStatus}
      handleDmFilterSelected={setDmFilter}
      sort={{ by: state.sort, direction: state.order }}
      handleSortClick={handleSortClick}
      pages={Math.ceil(state.dataCount / limit)}
      handlePageChange={setPage}
      spnFilterRef={spnFilterRef}
      fmiFilterRef={fmiFilterRef}
      saFilterRef={saFilterRef}
      canbusFilterRef={canbusFilterRef}
      error={error}
      setError={setError}
      showMobileFilters={showMobileFilters}
      toggleMobileFilters={toggleMobileFilters}
    />
  );
}

AssetDM1FaultsContainer.propTypes = {
  asset: PropTypes.object,
  currentUser: PropTypes.object,
  currentOrg: PropTypes.object,
  orgsMap: PropTypes.object,
  width: PropTypes.number,
};

AssetDM1FaultsContainer.defaultProps = { currentUser: {} };
